import MediaDocumentsListRaw from "@/components/Documents/MediaDocumentsListRaw.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { useDeleteNote } from "@/mutations/useDeleteNote.tsx";
import { useDeleteNotification } from "@/mutations/useDeleteNotification.tsx";
import { useToggleNotePin } from "@/mutations/useToggleNotePin.tsx";
import type { Note } from "@/queries/useFetchNotesForCase.ts";
import { useFetchNotificationsForUser } from "@/queries/useFetchNotificationsForUser.ts";
import type { NoteNotification } from "@/types/NoteNotification.ts";
import { getErrorMessage } from "@/utils/api.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";

import { AlertCircle, Bell } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import sanitizeHtml from "sanitize-html";

const dateFormatterDashed = Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZone: "America/New_York",
});

const renderLine = (line: string) => {
    const clean = sanitizeHtml(line, {
        allowedTags: ["a", "hr", "br"],
        allowedAttributes: {
            a: ["href"],
        },
        allowedSchemes: ["http", "https", "mailto"],
    });

    // Only use dangerouslySetInnerHTML if sanitization found/kept HTML
    if (clean !== line || clean.includes("<")) {
        return <span dangerouslySetInnerHTML={{ __html: clean }} />;
    }

    return line;
};

export const NoteItem = ({ note, caseIdUUID }: { note: Note; caseIdUUID: string }) => {
    const { hasPermission } = useEmployee();
    const deleteNote = useDeleteNote();
    const togglePinMutation = useToggleNotePin();
    const deleteNotificationMutation = useDeleteNotification();
    const navigate = useNavigate();
    const noteData = note.note;
    const {
        data: notifications = [],
        isError: isErrorNotifications,
        error: errorNotifications,
    } = useFetchNotificationsForUser();

    const notificationsForThisNote: NoteNotification | undefined = useMemo(() => {
        return notifications.find((notification) => notification.note?.idUUID === note.idUUID);
    }, [notifications, note]);

    const title = useMemo(() => {
        let title = `${note.noteBy} - ${
            note.metaCreatedWhen ? dateFormatterDashed.format(note.metaCreatedWhen) : ""
        }`;

        if (note.tag) {
            title += ` - ${note.tag}`;
        }

        if (note.caseTask) {
            title += ` (Task - ${note.caseTask.name})`;
        }

        return title;
    }, [note]);

    const handleDeleteNote = (noteUUID: string) => {
        if (window.confirm("Are you sure you want to delete this Note?")) {
            deleteNote.mutate(
                {
                    noteUUID: noteUUID,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Note removed.", { variant: "success" });
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: Note could not be removed.", { variant: "error" });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        }
    };

    const handleTogglePin = (noteId: string | null) => {
        if (!noteId) {
            enqueueSnackbar("Error: Note ID is missing.", { variant: "error" });
            return;
        }

        togglePinMutation.mutate(
            {
                noteId,
                caseIdUUID,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar(note.isPinned ? "Note unpinned." : "Note pinned to top.", {
                        variant: "success",
                    });
                },
                onError: (error) => {
                    enqueueSnackbar("Error: Could not update pin status.", {
                        variant: "error",
                    });
                    console.error("Toggle pin error:", getErrorMessage(error));
                },
            },
        );
    };

    const getPriorityLabel = (priority: string): string => {
        const priorityMap: Record<string, string> = {
            high: "High Priority Notification",
            medium: "Medium Priority Notification",
            low: "Low Priority Notification",
        };
        return priorityMap[priority] || "Notification";
    };

    const handleClearNotification = (notificationUUID: string, noteId?: string) => {
        deleteNotificationMutation.mutate(
            {
                notificationUUID,
                noteId,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Notification cleared.", { variant: "success" });
                },
                onError: (error) => {
                    enqueueSnackbar("Error: Notification could not be removed.", {
                        variant: "error",
                    });
                    console.info("error", getErrorMessage(error));
                },
            },
        );
    };

    // Handle clicks on internal links to use React Router navigation
    const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;

        // Check if the clicked element is an anchor tag
        if (target.tagName === "A") {
            const anchor = target as HTMLAnchorElement;
            const href = anchor.getAttribute("href");

            // Only intercept internal links (starting with /)
            if (href?.startsWith("/")) {
                e.preventDefault();
                navigate(href);
            }
        }
    };

    const pinTooltip = note.isPinned
        ? note.pinnedBy
            ? `Pinned by ${note.pinnedBy} - Click to unpin`
            : "Click to unpin"
        : "Pin note";

    return (
        <Card
            sx={{
                height: "100%",
                width: "100%",
                position: "relative",
                backgroundColor: note.isPinned ? "#e3f2fd" : "#ffffff",
                transition: "background-color 0.2s ease-in-out",
            }}
        >
            {note.isPinned && (
                <Tooltip title={pinTooltip} arrow>
                    <Box
                        onClick={() => handleTogglePin(note.idUUID)}
                        sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: 0,
                            height: 0,
                            borderStyle: "solid",
                            borderWidth: "0 48px 48px 0",
                            borderColor: "transparent #ff9800 transparent transparent",
                            cursor: "pointer",
                            zIndex: 1,
                            transition: "transform 0.2s ease-in-out",
                            "&:hover": {
                                transform: "scale(1.05)",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                top: "8px",
                                right: "-40px",
                                color: "#fff",
                                pointerEvents: "none",
                                lineHeight: 0,
                            }}
                        >
                            <PushPinIcon sx={{ fontSize: 16, transform: "rotate(45deg)" }} />
                        </Box>
                    </Box>
                </Tooltip>
            )}

            <Stack
                sx={{
                    flexDirection: "column",
                    backgroundColor: "#e0e1e1",
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    gap: 1,
                    borderBottom: "1px solid #ccc",
                }}
            >
                <Typography
                    variant="body1"
                    sx={{
                        flex: "1 1 auto",
                        minWidth: 0,
                    }}
                >
                    {title}
                </Typography>

                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        justifyContent: "center",
                        flexShrink: 0,
                        width: "100%",
                    }}
                >
                    <Tooltip title={pinTooltip} arrow>
                        <IconButton
                            onClick={() => handleTogglePin(note.idUUID)}
                            size="small"
                            disabled={togglePinMutation.isPending}
                            sx={{
                                color: note.isPinned ? "#ff9800" : "#666666",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                    color: "#ff9800",
                                    transform: "scale(1.1)",
                                },
                            }}
                        >
                            {togglePinMutation.isPending ? (
                                <CircularProgress size={18} sx={{ color: "#ff9800" }} />
                            ) : (
                                <PushPinOutlinedIcon sx={{ fontSize: 18, display: "block" }} />
                            )}
                        </IconButton>
                    </Tooltip>

                    {!isErrorNotifications &&
                        note.notifiedUsers &&
                        note.notifiedUsers.length > 0 && (
                            <Tooltip title={`Notified: ${note.notifiedUsers}`} arrow>
                                <IconButton
                                    size="small"
                                    sx={{
                                        color: (theme) => theme.palette.info.main,
                                        "&:hover": {
                                            backgroundColor: (theme) =>
                                                `${theme.palette.info.light}20`,
                                        },
                                    }}
                                >
                                    <Bell size={18} style={{ display: "block" }} />
                                </IconButton>
                            </Tooltip>
                        )}

                    {hasPermission("delete:note") && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();

                                if (note.idUUID) {
                                    handleDeleteNote(note.idUUID);
                                }
                            }}
                            size="small"
                            disabled={deleteNote.isPending}
                        >
                            {deleteNote.isPending ? (
                                <CircularProgress size={18} />
                            ) : (
                                <DeleteIcon sx={{ fontSize: 18, display: "block" }} />
                            )}
                        </IconButton>
                    )}
                </Stack>
            </Stack>

            <CardContent
                sx={{
                    backgroundColor: "#f2f2f2",
                    height: "100%",
                }}
            >
                {isErrorNotifications && (
                    <Alert severity="warning" sx={{ mb: 2 }} icon={<AlertCircle size={20} />}>
                        <Typography variant="body2">
                            Unable to load notifications:{" "}
                            {errorNotifications?.message || "An unexpected error occurred"}
                        </Typography>
                    </Alert>
                )}

                {!isErrorNotifications &&
                    notificationsForThisNote &&
                    notificationsForThisNote.priority && (
                        <Box
                            onClick={
                                notificationsForThisNote.hasUnreadNotification
                                    ? () =>
                                          handleClearNotification(
                                              notificationsForThisNote.idUUID,
                                              note.idUUID ?? undefined,
                                          )
                                    : undefined
                            }
                            title={
                                notificationsForThisNote.hasUnreadNotification
                                    ? "Click to clear notification"
                                    : "Notification cleared"
                            }
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                padding: (theme) => theme.spacing(1, 1.5),
                                borderRadius: (theme) => theme.spacing(0.5),
                                width: "100%",
                                marginBottom: (theme) => theme.spacing(1.5),
                                cursor: notificationsForThisNote.hasUnreadNotification
                                    ? "pointer"
                                    : "default",
                                fontWeight: 500,
                                fontSize: "0.875rem",
                                transition: "opacity 0.2s",
                                ...(notificationsForThisNote.hasUnreadNotification
                                    ? {
                                          backgroundColor:
                                              notificationsForThisNote.priority === "high"
                                                  ? "#d32f2f"
                                                  : notificationsForThisNote.priority === "medium"
                                                    ? "#ed6c02"
                                                    : "#4caf50",
                                          color: "#ffffff",
                                          "&:hover": {
                                              opacity: 0.85,
                                          },
                                      }
                                    : {
                                          backgroundColor: "transparent",
                                          border: "1px solid",
                                          borderColor: (theme) => theme.palette.grey[400],
                                          color: (theme) => theme.palette.grey[600],
                                      }),
                            }}
                        >
                            <Bell size={14} />
                            {notificationsForThisNote.title ||
                                getPriorityLabel(notificationsForThisNote.priority)}
                        </Box>
                    )}

                {noteData && (
                    <Typography variant={"body2"} onClick={handleLinkClick} sx={{ cursor: "auto" }}>
                        {noteData
                            .replace(/\r/g, "")
                            .split("\n")
                            .map((line, i) => {
                                const cleanLine = line.trim();

                                if (!cleanLine) {
                                    return <br key={`${note.idUUID}-${i}`} />;
                                }

                                return (
                                    <React.Fragment key={`${note.idUUID}-${i}`}>
                                        {renderLine(cleanLine)}
                                        {i !==
                                            noteData.replace(/\r/g, "").split("\n").length - 1 && (
                                            <br />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                    </Typography>
                )}

                {note.media.length > 0 && (
                    <Stack
                        sx={{
                            flexDirection: "column",
                            width: "100%",
                            backgroundColor: "#ddd",
                            borderRadius: "8px",
                            p: 1,
                            mt: 2,
                        }}
                    >
                        <Typography variant={"body2"} sx={{ fontWeight: 800, textAlign: "center" }}>
                            Attachments
                        </Typography>
                        <MediaDocumentsListRaw mediaArray={note.media} showDelete={false} />
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
};
